// External libraries
import {
  genericValidationFn,
  getMaskedPhoneNumber,
  validatePhone,
} from '@checkout/react-components';

// Internal modules
import { appStore } from '@/store';
import { $logger } from '@/lib/logger';
import { getLocale } from '@/lib/globals';
import { features } from '@/lib/features';
import { redirectToGenericError, redirectToXoon } from '@/lib/utils/redirects';
import { logFPTIWhenDomesticTransactionBlocked } from '../fpti';

const conditionallyUpdateShippingCountry = ({ country: merchantCountry }) => {
  const isDomesticShippingOnly = features.isActive('blockNonDomesticShipping');
  const buyerCountry = getLocale().country;
  const isNonDomesticTransaction =
    merchantCountry && merchantCountry !== buyerCountry;

  // If it is a domestic transaction or the merchant offers international
  // shipping then we do not need to update the shipping country
  if (!isNonDomesticTransaction || !isDomesticShippingOnly) {
    return;
  }

  $logger.info('weasley_non_domestic_transaction_with_restricted_shipping', {
    buyerCountry,
    merchantCountry,
  });

  // Fetch address metadata for merchant country
  import('@/components/Address/api')
    .then(addressApi => {
      addressApi.fetchAddressMetadata(merchantCountry);
    })
    .catch(({ message }) => {
      $logger.error('weasley_error_loading_address_api_file', { message });
      redirectToGenericError();
    });

  const { form: formActions } = appStore.getActions();
  formActions.updateShipToBilling(false);
  formActions.updateAddressField({
    addressType: 'shippingAddress',
    field: 'country',
    payload: { value: merchantCountry },
  });
};

/**
 * @function getBillingNameFromCart
 * @param {object} payer
 * @param {object} shippingAddress
 * @returns {object} { firstName, lastName }
 */
const getBillingNameFromCart = ({ payer, shippingAddress }) => {
  const { familyName, givenName } = payer?.name || {};
  if (givenName || familyName) {
    return {
      firstName: givenName,
      lastName: familyName,
    };
  }

  const { firstName, isStoreAddress, lastName } = shippingAddress || {};
  // If the shipping address is a store address then we don't want to
  // pre-fill the billing name fields with the store name
  if (isStoreAddress) {
    return {};
  }

  return { firstName, lastName };
};

/**
 * @function getPhoneValidator
 * @param {object} phoneMeta { mask | pattern }
 * @returns {function}
 * @description Generates a function that takes a string value
 *  for a phone number and returns a validation object.
 */
const getPhoneValidator = ({ mask, pattern }) => value => {
  const props = {
    maxLength: mask?.length,
    minLength: mask?.length,
    pattern,
    required: true,
  };
  const maskedValue = getMaskedPhoneNumber(value, mask);
  return validatePhone(value, { maskedValue, props });
};

const handleBlockedDomesticTransactions = () => {
  let {
    country: merchantCountry,
  } = appStore.getState().api.cartDetails.merchant;
  const { country: buyerCountry } = getLocale();

  // merchant country can sometimes come in as CN, we need to remap these to C2
  // since buyerCountry will always be C2
  merchantCountry = merchantCountry === 'CN' ? 'C2' : merchantCountry;

  if (
    features.isActive('shouldBlockDomesticTransactions') &&
    merchantCountry === buyerCountry
  ) {
    logFPTIWhenDomesticTransactionBlocked(merchantCountry);
    // Xoon doesn't block RU domestic transactions, so we need to show
    // a compliance violation error directly
    if (merchantCountry === 'RU') {
      return redirectToGenericError({ message: 'COMPLIANCE_VIOLATION' });
    }
    return redirectToXoon();
  }
};

/**
 * @function handleSignupAndGuestIneligible
 * @description Redirect to a generic error when both signup and guest are
 *  ineligible. This should only happen if the buyer is from a country that
 *  disabled signup and if the merchant is enforcing signup (e.g. gambling)
 */
const handleSignupAndGuestIneligible = () => {
  const isSignUpEligible = !features.isActive('shouldDisableSignup');
  const isGuestEligible = features.isActive('isGuestEligible');

  if (!isSignUpEligible && !isGuestEligible) {
    $logger.error('weasley_error_signup_and_guest_ineligible', {
      buyerCountry: getLocale().country,
    });
    redirectToGenericError();
  }
};

/**
 * @function validateEmail
 * @param {string} value
 * @returns {object}
 */
const validateEmail = value => {
  const props = { required: true, type: 'email' };
  return genericValidationFn(value, { props });
};

export {
  conditionallyUpdateShippingCountry,
  getBillingNameFromCart,
  getPhoneValidator,
  handleBlockedDomesticTransactions,
  handleSignupAndGuestIneligible,
  validateEmail,
};
